import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const StudentAbsences = () => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [motif, setMotif] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAbsences();
  }, []);

  const fetchAbsences = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/student/absences', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAbsences(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (abs) => {
    setSelectedAbsence(abs);
    setMotif(abs.reason || '');
    setFile(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAbsence(null);
    setMotif('');
    setFile(null);
  };

  const handleSubmit = async () => {
    if (!motif.trim()) {
      alert('Le motif est obligatoire');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('reason', motif);
      if (file) formData.append('file', file);

      await api.post(`/student/absences/${selectedAbsence.id}/justify`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });

      alert('✅ Demande envoyée ! En attente de validation.');
      closeModal();
      fetchAbsences();
    } catch (err) {
      alert('❌ Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredAbsences = absences.filter((abs) => {
    if (filterType && abs.type !== filterType) return false;
    if (filterStatus === 'justified' && !abs.is_justified) return false;
    if (filterStatus === 'not_justified' && (abs.is_justified || abs.justification_status === 'pending')) return false;
    if (filterStatus === 'pending' && abs.justification_status !== 'pending') return false;
    return true;
  });

  const totalAbsences = absences.filter(a => a.type === 'absence').length;
  const totalRenvoyes = absences.filter(a => a.type === 'renvoye').length;
  const justifiedCount = absences.filter(a => a.is_justified).length;
  const pendingCount = absences.filter(a => a.justification_status === 'pending').length;
  const notJustifiedCount = absences.filter(a => !a.is_justified && a.justification_status !== 'pending').length;

  const getStatusBadge = (abs) => {
    if (abs.is_justified) return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">✅ Justifiée</span>;
    if (abs.justification_status === 'pending') return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">⏳ En attente</span>;
    if (abs.justification_status === 'rejected') return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">❌ Refusée</span>;
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">🔴 Non justifiée</span>;
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'absence':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">🚨 Absence</span>;
      case 'retard':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">⏰ Retard</span>;
      case 'renvoye':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">🚪 Renvoyé</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{type}</span>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">📅 Mes absences et retards</h1>
        <p className="text-gray-500">Consultez et justifiez vos absences</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <p className="text-sm text-gray-500">Total absences</p>
          <p className="text-2xl font-bold text-blue-600">{totalAbsences}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-purple-500">
          <p className="text-sm text-gray-500">Renvoyés</p>
          <p className="text-2xl font-bold text-purple-600">{totalRenvoyes}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
          <p className="text-sm text-gray-500">Justifiées</p>
          <p className="text-2xl font-bold text-green-600">{justifiedCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
          <p className="text-sm text-gray-500">En attente</p>
          <p className="text-2xl font-bold text-orange-600">{pendingCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
          <p className="text-sm text-gray-500">Non justifiées</p>
          <p className="text-2xl font-bold text-red-600">{notJustifiedCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="">Tous les types</option>
            <option value="absence">🚨 Absences</option>
            <option value="retard">⏰ Retards</option>
            <option value="renvoye">🚪 Renvoyés</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
            <option value="">Tous les statuts</option>
            <option value="justified">✅ Justifiées</option>
            <option value="pending">⏳ En attente</option>
            <option value="not_justified">🔴 Non justifiées</option>
          </select>
          <button onClick={() => { setFilterType(''); setFilterStatus(''); }} className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300">🔄 Réinitialiser</button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">Chargement...</div>
      ) : filteredAbsences.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
          <p className="text-lg mb-2">🎉 Aucune absence trouvée</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-bold text-[#2C3E50]">📋 Historique ({filteredAbsences.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date / Heure</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Durée</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Motif</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAbsences.map((abs) => (
                  <tr key={abs.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium">
                      {abs.date} {abs.time && <span className="text-gray-400 text-xs ml-1">à {abs.time}</span>}
                    </td>
                    <td className="py-3 px-4">{getTypeBadge(abs.type)}</td>
                    <td className="py-3 px-4 text-sm">{abs.duration_minutes ? `${abs.duration_minutes} min` : '-'}</td>
                    <td className="py-3 px-4">{getStatusBadge(abs)}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">{abs.reason || '-'}</td>
                    <td className="py-3 px-4">
                      {!abs.is_justified && abs.justification_status !== 'pending' && (
                        <button
                          onClick={() => openModal(abs)}
                          className="px-3 py-1.5 bg-[#F39C12] text-white rounded-lg text-xs font-medium hover:bg-[#E67E22]"
                        >
                          📎 Justifier
                        </button>
                      )}
                      {abs.justification_status === 'pending' && (
                        <span className="text-xs text-orange-600">⏳ En attente</span>
                      )}
                      {abs.is_justified && (
                        <span className="text-xs text-green-600">✅ Validée</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL JUSTIFICATION */}
      {showModal && selectedAbsence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold text-[#2C3E50] mb-4">📎 Justifier l'absence</h2>

            <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm">
              <p><strong>Date :</strong> {selectedAbsence.date} {selectedAbsence.time && `à ${selectedAbsence.time}`}</p>
              <p><strong>Type :</strong> {
                selectedAbsence.type === 'absence' ? '🚨 Absence' : 
                selectedAbsence.type === 'renvoye' ? '🚪 Renvoi de cours' : 
                '⏰ Retard'
              }</p>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">Motif *</label>
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              rows="3"
              placeholder="Ex: Maladie, rendez-vous médical, raison familiale..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#F39C12] mb-4"
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Justificatif (PDF ou image, max 5 Mo)</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#F39C12] mb-4"
            />

            <div className="flex justify-end gap-3">
              <button onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 bg-[#F39C12] text-white rounded-lg hover:bg-[#E67E22] font-medium disabled:opacity-50"
              >
                {submitting ? 'Envoi...' : '📤 Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAbsences;